import { JSX, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { mcpServerOptions } from "@via/utils/form-validators.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import McpServerHeadersInput from "@via/pages/cortex/mcp-server/mcp-server-headers-input.tsx";
import MonacoEditor from "@via/components/monaco-editor/monaco-editor.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import { useTestMcpConnection } from "@via/pages/cortex/mcp-server/mcp-server.api.tsx";
import {
  CreateMcpServerRequestBody,
  McpServerDetail,
  McpTransportType,
  UpdateMcpServerRequestBody,
} from "common";

interface McpServerFormProps {
  setOpen: (open: boolean) => void;
  onSubmit: (
    payload: CreateMcpServerRequestBody | UpdateMcpServerRequestBody
  ) => void;
  isLoading?: boolean;
  /** When provided, the form switches to edit mode with pre-filled values */
  editData?: McpServerDetail | null;
}

interface HeaderRow {
  key: string;
  value: string;
}

interface McpServerFormValues {
  name: string;
  serverUrl: string;
  transportType: string;
  description: string;
  headers: HeaderRow[];
  advancedConfig: string;
}

const transportTypeOptions: SelectOption[] = [
  { label: "HTTP Streamable", value: McpTransportType.HttpStreamable },
  { label: "Server-Sent Events (SSE)", value: McpTransportType.SSE },
  { label: "WebSocket", value: McpTransportType.WebSocket },
];

const headersArrayToRecord = (
  headers: HeaderRow[]
): Record<string, string> | undefined => {
  const filtered = headers.filter(h => h.key.trim() !== "");
  if (filtered.length === 0) return undefined;
  return Object.fromEntries(filtered.map(h => [h.key, h.value]));
};

const headersRecordToArray = (
  headers: Record<string, string> | null | undefined
): HeaderRow[] => {
  if (!headers || Object.keys(headers).length === 0) return [];
  return Object.entries(headers).map(([key, value]) => ({ key, value }));
};

const getDefaultFormValues = (
  editData?: McpServerDetail | null
): McpServerFormValues => {
  if (editData) {
    return {
      name: editData.name,
      serverUrl: editData.serverUrl || "",
      transportType: editData.transportType,
      description: editData.description || "",
      headers: headersRecordToArray(editData.headers),
      advancedConfig: editData.advancedConfig
        ? JSON.stringify(editData.advancedConfig, null, 2)
        : "{}",
    };
  }
  return {
    name: "",
    serverUrl: "",
    transportType: McpTransportType.HttpStreamable,
    description: "",
    headers: [],
    advancedConfig: "{}",
  };
};

const parseAdvancedConfig = (json: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(json);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length > 0
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

const McpServerForm = ({
  setOpen,
  onSubmit,
  isLoading = false,
  editData = null,
}: McpServerFormProps): JSX.Element => {
  const editorRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(!!editData?.advancedConfig);

  const {
    mutate: testConnection,
    data: testResult,
    isPending: isTestingConnection,
    reset: resetTestConnection,
  } = useTestMcpConnection();

  const isEditMode = !!editData;

  const {
    control,
    handleSubmit,
    reset,
    register,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<McpServerFormValues>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: getDefaultFormValues(editData),
  });

  // Register advancedConfig with conditional validation
  const advancedConfigRegistration = register(
    "advancedConfig",
    showAdvanced ? mcpServerOptions.advancedConfig : {}
  );

  // Reset form when editData changes
  useEffect(() => {
    reset(getDefaultFormValues(editData));
    setShowAdvanced(!!editData?.advancedConfig);
  }, [editData, reset]);

  // Clear advancedConfig errors when toggling off
  useEffect(() => {
    if (!showAdvanced && errors.advancedConfig) {
      trigger("advancedConfig");
    }
  }, [showAdvanced, trigger, errors.advancedConfig]);

  const handleClose = (): void => {
    reset();
    setShowAdvanced(false);
    resetTestConnection();
    setOpen(false);
  };

  const handleTestConnection = (): void => {
    const formValues = getValues();

    if (showAdvanced) {
      const advancedConfig = parseAdvancedConfig(formValues.advancedConfig);
      testConnection({
        advancedConfig: advancedConfig ?? undefined,
        transportType: formValues.transportType as McpTransportType,
      });
    } else {
      const headersRecord = headersArrayToRecord(formValues.headers ?? []);
      testConnection({
        serverUrl: formValues.serverUrl,
        headers: headersRecord,
        transportType: formValues.transportType as McpTransportType,
      });
    }
  };

  const onFormSubmit = handleSubmit((data: McpServerFormValues) => {
    if (showAdvanced) {
      const advancedConfig = parseAdvancedConfig(data.advancedConfig);
      if (isEditMode) {
        const payload: UpdateMcpServerRequestBody = {
          name: data.name,
          description: data.description || undefined,
          advancedConfig: advancedConfig,
        };
        onSubmit(payload);
      } else {
        const payload: CreateMcpServerRequestBody = {
          name: data.name,
          description: data.description || undefined,
          advancedConfig: advancedConfig ?? undefined,
        };
        onSubmit(payload);
      }
    } else {
      const headersRecord = headersArrayToRecord(data.headers ?? []);
      if (isEditMode) {
        const payload: UpdateMcpServerRequestBody = {
          name: data.name,
          serverUrl: data.serverUrl,
          transportType: data.transportType as McpTransportType,
          description: data.description || undefined,
          headers: headersRecord || null,
        };
        onSubmit(payload);
      } else {
        const payload: CreateMcpServerRequestBody = {
          name: data.name,
          serverUrl: data.serverUrl,
          transportType: data.transportType as McpTransportType,
          description: data.description || undefined,
          headers: headersRecord,
        };
        onSubmit(payload);
      }
    }
  });

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onFormSubmit(e);
      }}>
      {/* Header */}
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {isEditMode ? "Edit MCP Server" : "New MCP Server"}
            </Dialog.Title>
            <button
              type="button"
              className="relative rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={handleClose}>
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {isEditMode
              ? "Update the MCP server configuration."
              : "Register a new MCP server to use with AI Insights."}
          </p>
        </div>

        {/* Form fields */}
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-2 sm:px-4">
            <div className="space-y-6 pb-5 pt-6">
              {/* JSON Configuration toggle */}
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm text-gray-600">
                  JSON Configuration
                </span>
                <ToggleButton
                  isOn={showAdvanced}
                  onToggle={() => setShowAdvanced(!showAdvanced)}
                />
              </div>

              <div>
                <FormInputComponent
                  control={control}
                  name="name"
                  label="Name"
                  showRequiredField={true}
                  placeholder="e.g., NCBI/PubMed, UniProt, GitHub, etc."
                  validations={mcpServerOptions.name}
                />
              </div>

              {!showAdvanced && (
                <div>
                  <FormInputComponent
                    control={control}
                    name="serverUrl"
                    label="Server URL"
                    showRequiredField={true}
                    placeholder="https://api.example.com/mcp"
                    validations={mcpServerOptions.serverUrl}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    The endpoint URL for the MCP server
                  </p>
                </div>
              )}

              {!showAdvanced && (
                <McpServerHeadersInput control={control} name="headers" />
              )}
              {!showAdvanced && (
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
                    Transport Type
                    <span className="text-red-400">&nbsp;*</span>
                  </label>
                  <SelectComponent
                    control={control}
                    name="transportType"
                    rules={mcpServerOptions.transportType}
                    options={transportTypeOptions}
                    placeholder="Select transport type"
                    enableClear={false}
                  />
                  {errors.transportType && (
                    <span className="text-xs text-rose-500">
                      {errors.transportType.message || "This field is required"}
                    </span>
                  )}
                </div>
              )}

              {showAdvanced && (
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium leading-6 text-gray-900">
                    JSON Configuration
                    <span className="text-red-400">&nbsp;*</span>
                  </label>
                  <p className="mb-2 text-sm text-gray-500">
                    Provide the full MCP server configuration as JSON. This
                    takes precedence over form fields at runtime.
                  </p>
                  <input type="hidden" {...advancedConfigRegistration} />
                  <div className="border rounded-md overflow-hidden">
                    <MonacoEditor
                      editorRef={editorRef}
                      language="json"
                      value={getDefaultFormValues(editData).advancedConfig}
                      onChange={value => {
                        setValue("advancedConfig", value || "", {
                          shouldValidate: true,
                        });
                      }}
                      height="180px"
                      options={{
                        minimap: { enabled: false },
                        lineNumbers: "on",
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        fontSize: 13,
                        scrollbar: {
                          alwaysConsumeMouseWheel: false,
                        },
                      }}
                    />
                  </div>
                  {errors.advancedConfig && (
                    <span className="text-xs text-rose-500">
                      {errors.advancedConfig.message}
                    </span>
                  )}
                </div>
              )}

              <div>
                <FormTextAreaComponent
                  control={control}
                  name="description"
                  label="Description"
                  placeholder="Brief description of what this MCP server provides..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Test Connection Result */}
      {!isTestingConnection && testResult && (
        <div className="mx-4 mb-2">
          <MessageBanner
            type={testResult.success ? BannerType.Success : BannerType.Error}
            title={
              testResult.success ? "Connection Successful" : "Connection Failed"
            }
            size={BannerSize.Small}
            messages={
              testResult.success
                ? testResult.serverInfo
                  ? `Server: ${testResult.serverInfo.name} v${testResult.serverInfo.version} · ${testResult.toolCount ?? 0} tool${testResult.toolCount === 1 ? "" : "s"} · ${testResult.latencyMs}ms`
                  : `Connected · ${testResult.toolCount ?? 0} tool${testResult.toolCount === 1 ? "" : "s"} · ${testResult.latencyMs}ms`
                : (testResult.error ?? "Could not connect to the server")
            }
          />
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-shrink-0 justify-between px-4 py-4">
        <button
          type="button"
          disabled={isTestingConnection}
          onClick={handleTestConnection}
          className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          <Spinner loading={isTestingConnection}>Test Connection</Spinner>
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <Spinner loading={isLoading}>
              {isEditMode ? "Save Changes" : "Add MCP Server"}
            </Spinner>
          </button>
        </div>
      </div>
    </form>
  );
};

export default McpServerForm;
